/**
 * SageLanding — public marketing page for Sage, the AI Career Copilot.
 *
 * Public, NOT auth-gated. Target SERP intent:
 *   - "ai career copilot" / "ai career coach"
 *   - "chatgpt for job search" / "ai resume advice"
 *   - "career coach alternative" / "career coach for resumes"
 *
 * Companion to /coach (the actual chat surface, auth-gated). This page is
 * the SEO entry point + the conversion landing for the Sage feature. The
 * primary CTA pushes to `/register?next=/coach` so signup → land directly
 * in the Sage chat with their resume context already loaded.
 *
 * Page anatomy:
 *   - Hero: badge → H1 → subhead → dual CTA
 *   - Honesty band: matches /landing-page (Transparent / No Signup Wall / Your Data)
 *   - "What Sage does" — 6 capability cards
 *   - Sample conversation preview (real-looking Q + A pair)
 *   - "Why Sage" vs human coach mini-comparison
 *   - 5-question FAQ (FAQPage JSON-LD eligible)
 *   - Final CTA strip
 *
 * SEO metadata baked at build time via seo-routes.js (prerender), with
 * useDocumentMeta as the runtime/SPA-fallback safety net. Canonical
 * is owned by prerender — NEVER written client-side.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkles, CheckCircle2, MessageSquare, ShieldCheck,
  Clock, BadgeDollarSign, Eye, Lock, Wand2, Briefcase, Target,
  TrendingUp, FileText,
} from 'lucide-react';
import { useDocumentMeta } from '../utils/useDocumentMeta';

const CANONICAL = 'https://ascendforyou.net/sage';
const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const Section = ({ children, className = '' }) => (
  <section className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const CAPABILITIES = [
  {
    icon: Wand2,
    title: 'Rewrite weak bullets',
    body: 'Paste a bullet. Sage rewrites it with strong verbs + metrics that recruiters actually scan for. No fluff, no buzzwords.',
  },
  {
    icon: Target,
    title: 'Match a job description',
    body: 'Drop any JD. Sage tells you which keywords your resume is missing and where to place them — without keyword-stuffing.',
  },
  {
    icon: MessageSquare,
    title: 'Interview prep',
    body: 'Practice answers grounded in YOUR resume. STAR structure, role-specific hard questions, follow-up coaching.',
  },
  {
    icon: BadgeDollarSign,
    title: 'Salary negotiation',
    body: 'Get a counter-offer script written for the exact role, level, and city — calibrated to market comp data.',
  },
  {
    icon: Briefcase,
    title: 'Application priorities',
    body: 'Ask "what should I focus on this week?" Sage reads your resume + target role and gives you a 3-item plan.',
  },
  {
    icon: TrendingUp,
    title: 'LinkedIn polish',
    body: 'Headline, About section, and experience bullets rewritten for both ATS scrapers and human recruiters scanning the page.',
  },
];

const SAMPLE_CONVERSATION = {
  user: "I have a Senior PM interview at a Series-B fintech in 3 days. My last role was at a healthcare SaaS. What's the one thing I should prep that 90% of candidates miss?",
  sage: `Honest answer: the metric translation, not the case study.\n\nFintech PMs interview heavily on unit economics + risk framing. Healthcare-SaaS candidates default to talking about adoption and NPS — which sounds soft to a fintech panel.\n\nDo this:\n1. Pick 1-2 bullets from your healthcare resume and rewrite them in fintech vocabulary BEFORE the interview. E.g. "reduced ED throughput time" → "shortened claim-cycle SLA, recovering $X in working capital".\n2. Prep a 60-second answer to "what's the riskiest assumption in our current product?" — they ARE going to ask it.\n3. Know the company's last funding round size and read their Crunchbase 10x more carefully than you think you need to.\n\nWant me to rewrite a specific bullet from your resume in fintech framing?`,
};

const FAQS = [
  ['Is Sage really free to try?',
   'Yes. You can chat with Sage on the free plan (5 messages per day). Paid plans remove the daily cap and keep full resume context across all your resumes.'],
  ['How is Sage different from ChatGPT?',
   "ChatGPT doesn't know your resume, your target role, your career history, or your ATS score. Sage does — every reply is grounded in YOUR documents and the role you're targeting. No copy-pasting context into a prompt every time."],
  ['Does Sage replace a human career coach?',
   'For 80% of questions — bullet rewriting, interview drills, ATS keyword checks, salary research — yes, and it does it 24/7 for less than one coaching session. For deep career-pivot strategy with a human, no. We tell you when to hire a human.'],
  ['Is my resume data private?',
   'Yes. Sage only reads YOUR resumes, only when you ask. We do not train models on your data, do not sell it, and you can delete your account and all stored documents from the dashboard at any time.'],
  ['What can I ask Sage that I cannot ask other AI tools?',
   'Anything that requires understanding YOUR specific career context: "rewrite bullet #3 with a quantified outcome", "what should I prep this week for the SRE interview", "is my resume missing keywords from this JD I just pasted". Generic LLMs need you to paste all that context manually. Sage already has it.'],
];

const SageLanding = () => {
  useDocumentMeta({
    title: 'Sage — Your Honest AI Career Copilot | Ascend',
    description: 'Sage is an AI career copilot that already knows your resume. Rewrite bullets, prep interviews, find ATS keyword gaps, negotiate salary — honest, transparent, free to try.',
    path: '/sage',
    imageAlt: 'Sage AI career copilot chatting with a job seeker about resume bullets and interview prep',
  });

  return (
    <div className="min-h-screen bg-white" data-testid="sage-landing">
      {/* Slim nav — minimal so the hero gets full focus */}
      <nav className="border-b border-[#e5e7eb] bg-white sticky top-0 z-50">
        <Section className="flex items-center justify-between py-4">
          <Link to="/" className="font-satoshi text-xl font-black tracking-tight text-[#0f1115]" data-testid="logo-home">
            ASCEND
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-[#4b5563] hover:text-[#002fa7]" data-testid="nav-login">
              Sign in
            </Link>
            <Link
              to="/register?next=/coach"
              className="bg-[#002fa7] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#001f6f] transition-colors"
              data-testid="nav-cta-try-sage"
            >
              Try Sage Free
            </Link>
          </div>
        </Section>
      </nav>

      {/* Hero */}
      <Section className="py-12 sm:py-16 lg:py-20">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#eff6ff] text-[#1e3a8a] text-xs font-semibold border border-[#bfdbfe]"
            data-testid="hero-badge"
          >
            <Sparkles className="w-3.5 h-3.5" /> MEET SAGE — YOUR AI CAREER COPILOT
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-satoshi font-black tracking-tight text-[#0f1115] mt-4 leading-[1.05]"
            data-testid="hero-h1"
          >
            An AI career coach
            <span className="block text-[#002fa7]">that already knows your resume.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#4b5563] leading-relaxed">
            Sage reads your resume, your target role, and your ATS score — then helps you rewrite bullets, prep interviews, and land the job. Always-on. Honest. Free to try, no credit card.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/register?next=/coach"
              className="inline-flex items-center gap-2 bg-[#002fa7] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#001f6f] transition-colors"
              data-testid="hero-cta-try-sage"
            >
              <MessageSquare className="w-4 h-4" /> Try Sage Free
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/free-ats-check"
              className="inline-flex items-center gap-2 border border-[#e5e7eb] text-[#0f1115] px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#f9fafb] transition-colors"
              data-testid="hero-secondary-cta"
            >
              Score My Resume First <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-4 text-xs text-[#6b7280] flex items-center gap-2" data-testid="hero-microtrust">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#10b981]" /> Free tier · Honest answers · Your data stays yours
          </p>
        </motion.div>
      </Section>

      {/* Honesty band — mirrors LandingPage manifesto so the brand voice is consistent */}
      <Section className="py-10 border-t border-[#e5e7eb] bg-[#fafafa]">
        <div className="grid sm:grid-cols-3 gap-6" data-testid="honesty-band">
          {[
            {
              icon: Eye,
              h: 'Transparent',
              p: "You see the reasoning, not just the answer. Sage explains WHY a bullet is weak before rewriting it.",
            },
            {
              icon: ShieldCheck,
              h: 'No upsell theatre',
              p: 'No fake countdowns. No "unlock for $9". The free tier is genuinely useful — paid tiers are for power users.',
            },
            {
              icon: Lock,
              h: 'Your data, your control',
              p: 'We never train models on your data. Delete everything from your dashboard any time. Period.',
            },
          ].map((item) => (
            <div key={item.h} className="bg-white border border-[#e5e7eb] rounded-lg p-5" data-testid={`honesty-${item.h.toLowerCase().split(' ')[0]}`}>
              <item.icon className="w-5 h-5 text-[#002fa7] mb-3" />
              <h3 className="font-satoshi text-base font-bold text-[#0f1115]">{item.h}</h3>
              <p className="mt-2 text-sm text-[#4b5563] leading-relaxed">{item.p}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* What Sage does */}
      <Section className="py-14 border-t border-[#e5e7eb]">
        <div className="mb-8 max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-3 block">
            WHAT SAGE DOES
          </span>
          <h2 className="font-satoshi text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1115] leading-tight">
            Six things Sage handles better than a generic AI chatbot.
          </h2>
          <p className="mt-3 text-sm text-[#6b7280] leading-relaxed">
            Each capability is grounded in your actual resume + the role you&apos;re targeting — not a blank prompt window.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="capability-grid">
          {CAPABILITIES.map((c) => (
            <div
              key={c.title}
              className="border border-[#e5e7eb] rounded-lg p-5 hover:border-[#002fa7] transition-colors"
              data-testid={`capability-${c.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <c.icon className="w-5 h-5 text-[#002fa7] mb-3" />
              <h3 className="font-satoshi text-base font-bold text-[#0f1115]">{c.title}</h3>
              <p className="mt-2 text-sm text-[#4b5563] leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Sample conversation preview — shows the voice + depth in a single screenshot.
          Static markup (not a real chat) so crawlers can index it for long-tail
          "ai career advice fintech pm" type queries. */}
      <Section className="py-14 border-t border-[#e5e7eb] bg-[#f9fafb]">
        <div className="mb-8 max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-3 block">
            SAMPLE CONVERSATION
          </span>
          <h2 className="font-satoshi text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1115] leading-tight">
            What honest career advice looks like.
          </h2>
        </div>
        <div className="max-w-3xl space-y-4" data-testid="sample-conversation">
          <div className="flex justify-end">
            <div className="max-w-[80%] px-5 py-3 text-sm leading-relaxed bg-[#002fa7] text-white rounded-lg" data-testid="sample-user-msg">
              {SAMPLE_CONVERSATION.user}
            </div>
          </div>
          <div className="flex justify-start">
            <div className="max-w-[88%] px-5 py-4 text-sm leading-relaxed bg-white border border-[#e5e7eb] text-[#0f1115] rounded-lg whitespace-pre-wrap" data-testid="sample-sage-msg">
              <div className="flex items-center gap-2 mb-2 text-[#002fa7]">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-xs uppercase tracking-[0.15em] font-bold">Sage</span>
              </div>
              {SAMPLE_CONVERSATION.sage}
            </div>
          </div>
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/register?next=/coach"
            className="inline-flex items-center gap-2 bg-[#002fa7] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#001f6f] transition-colors"
            data-testid="sample-cta-try-sage"
          >
            Try Sage Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      {/* Why Sage — small honest comparison vs human coach */}
      <Section className="py-14 border-t border-[#e5e7eb]">
        <div className="mb-8 max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-3 block">
            SAGE vs A HUMAN CAREER COACH
          </span>
          <h2 className="font-satoshi text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1115] leading-tight">
            We&apos;ll tell you which one you need.
          </h2>
          <p className="mt-3 text-sm text-[#6b7280] leading-relaxed">
            Honest comparison. We don&apos;t pretend Sage replaces deep human strategy — but for 80% of job-search questions, it&apos;s faster, cheaper, and always available.
          </p>
        </div>
        <div className="overflow-hidden border border-[#e5e7eb] rounded-lg" data-testid="comparison-table">
          <table className="w-full text-sm">
            <thead className="bg-[#f9fafb]">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-[#0f1115]">Use case</th>
                <th className="text-left px-4 py-3 font-bold text-[#002fa7]">Sage</th>
                <th className="text-left px-4 py-3 font-bold text-[#6b7280]">Human Coach</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e7eb]">
              {[
                ['Rewrite a resume bullet at 11pm', 'Instant, free', '$150-300/session, next week'],
                ['ATS keyword analysis vs a specific JD', 'Instant, exact match scoring', 'Manual, often guessed'],
                ['Interview prep drills', 'Role-specific, on-demand', 'Limited by session length'],
                ['Career pivot from healthcare → fintech', 'Tactical translation, not strategic life advice', 'Wins on long-term strategy'],
                ['Salary negotiation script', 'Calibrated to role + market', 'Wins if coach knows YOUR industry'],
                ['Emotional support during a layoff', 'We will say "talk to a human"', 'Wins — no question'],
              ].map(([useCase, sage, human], i) => (
                <tr key={i} data-testid={`compare-row-${i}`}>
                  <td className="px-4 py-3 text-[#0f1115]">{useCase}</td>
                  <td className="px-4 py-3 text-[#0f1115]">{sage}</td>
                  <td className="px-4 py-3 text-[#6b7280]">{human}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-14 border-t border-[#e5e7eb] bg-[#f9fafb]">
        <h2 className="font-satoshi text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1115] mb-8" data-testid="faq-h2">
          Frequently asked questions
        </h2>
        <div className="space-y-4 max-w-3xl">
          {FAQS.map(([q, a], i) => (
            <details
              key={i}
              className="bg-white border border-[#e5e7eb] rounded-lg p-5 group"
              data-testid={`faq-${i}`}
            >
              <summary className="font-semibold text-[#0f1115] cursor-pointer flex items-start gap-2">
                <FileText className="w-4 h-4 text-[#002fa7] flex-shrink-0 mt-0.5" />
                {q}
              </summary>
              <p className="mt-3 text-sm text-[#4b5563] leading-relaxed pl-6 whitespace-pre-wrap">{a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="py-16">
        <div className="bg-gradient-to-br from-[#0f1115] to-[#1f2937] text-white rounded-2xl p-8 sm:p-12 text-center" data-testid="footer-cta-strip">
          <div className="flex items-center justify-center gap-2 mb-3 text-[#bfdbfe]">
            <Clock className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">60-SECOND SIGNUP</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-satoshi font-bold">
            Meet your honest AI career copilot.
          </h2>
          <p className="mt-3 text-[#d1d5db] max-w-xl mx-auto">
            Sign up, upload your resume, ask Sage anything. Free tier. No credit card. No upsell theatre.
          </p>
          <Link
            to="/register?next=/coach"
            className="mt-6 inline-flex items-center gap-2 bg-white text-[#0f1115] px-6 py-3 rounded-lg text-sm font-bold hover:bg-[#f3f4f6] transition-colors"
            data-testid="footer-cta-try-sage"
          >
            <Sparkles className="w-4 h-4" /> Try Sage Free
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      <footer className="border-t border-[#e5e7eb] py-8">
        <Section>
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[#6b7280]">
            <span>© 2026 Ascend. The honest AI career copilot.</span>
            <div className="flex items-center gap-4">
              <Link to="/blog" className="hover:text-[#002fa7]">Blog</Link>
              <Link to="/pricing" className="hover:text-[#002fa7]">Pricing</Link>
              <Link to="/free-ats-check" className="hover:text-[#002fa7]">Free ATS Check</Link>
              <Link to="/features" className="hover:text-[#002fa7]">Features</Link>
            </div>
          </div>
          {/* Visible canonical hint for human auditors. Real <link rel="canonical">
              is emitted by prerender.js at build time. */}
          <p className="mt-4 text-[10px] text-[#9ca3af]">Canonical: {CANONICAL}</p>
        </Section>
      </footer>
    </div>
  );
};

export default SageLanding;
